import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config/config';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';
import './VigilRuleDetail.css';

/* ── severity palette ────────────────────────────────────────────────────────── */
const SEV = {
  critical: { color: '#dc2626', bg: 'rgba(220,38,38,.12)', label: 'Critical', icon: '🔴' },
  high:     { color: '#ea580c', bg: 'rgba(234,88,12,.12)',  label: 'High',     icon: '🟠' },
  medium:   { color: '#ca8a04', bg: 'rgba(202,138,4,.12)',  label: 'Medium',   icon: '🟡' },
  low:      { color: '#2563eb', bg: 'rgba(37,99,235,.12)',  label: 'Low',      icon: '🔵' },
  info:     { color: '#6b7280', bg: 'rgba(107,114,128,.12)',label: 'Info',     icon: '⚪' },
};

const CONFIDENCE_COLOR = {
  high: '#059669', medium: '#ca8a04', low: '#9ca3af',
};

const LANG_ICONS = {
  go: '🐹', java: '☕', python: '🐍', javascript: 'JS', typescript: 'TS',
  ruby: '💎', php: 'PHP', 'c': 'C', 'c++': 'C++', cpp: 'C++',
  rust: '🦀', csharp: 'C#', kotlin: '🟣', swift: '🍎',
  dockerfile: '🐳', yaml: '📄', hcl: '🏗️', generic: '📝',
};

function SeverityBadge({ severity }) {
  const s = (severity || 'info').toLowerCase();
  const cfg = SEV[s] || SEV.info;
  return (
    <span className="vrd-badge" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}40` }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function TagBadge({ label, color = '#6b7280' }) {
  return (
    <span className="vrd-tag" style={{ background: `${color}18`, color, border: `1px solid ${color}40` }}>
      {label}
    </span>
  );
}

function CodeBlock({ code, language = '' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="vrd-code-block">
      <div className="vrd-code-header">
        <span className="vrd-code-lang">{language || 'vigil'}</span>
        <button className="vrd-copy-btn" onClick={copy}>{copied ? '✓ Copied' : '📋 Copy'}</button>
      </div>
      <pre className="vrd-code-pre"><code>{code}</code></pre>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="vrd-section">
      <h2 className="vrd-section-title">{icon && <span>{icon}</span>} {title}</h2>
      {children}
    </section>
  );
}

export default function VigilRuleDetail() {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ruleId) return;
    setLoading(true);
    axios.get(`${config.BASE_URL}/vigil/rules/${encodeURIComponent(ruleId)}`)
      .then(res => setRule(res.data))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [ruleId]);

  if (loading) return <div className="vrd-container"><Skeleton lines={12} /></div>;
  if (error)   return <div className="vrd-container"><EmptyState icon="⚠️" title="Rule not found" description={error} action={{ label: '← Back to rules', onClick: () => navigate('/vigil/rules') }} /></div>;
  if (!rule)   return null;

  const sev = (rule.severity || 'info').toLowerCase();
  const sevCfg = SEV[sev] || SEV.info;

  return (
    <div className="vrd-container">
      {/* Breadcrumb */}
      <nav className="vrd-breadcrumb">
        <Link to="/vigil/rules">Rules</Link>
        <span className="vrd-bc-sep">›</span>
        {rule.packId && <><Link to={`/vigil/rules?pack=${rule.packId}`}>{rule.packId}</Link><span className="vrd-bc-sep">›</span></>}
        <span className="vrd-bc-current">{rule.id}</span>
      </nav>

      {/* Header */}
      <div className="vrd-header" style={{ borderLeft: `4px solid ${sevCfg.color}` }}>
        <div className="vrd-header-top">
          <h1 className="vrd-rule-id">{rule.id}</h1>
          <div className="vrd-header-badges">
            <SeverityBadge severity={rule.severity} />
            {rule.confidence && (
              <span className="vrd-badge" style={{ background: `${CONFIDENCE_COLOR[rule.confidence] || '#9ca3af'}18`, color: CONFIDENCE_COLOR[rule.confidence] || '#9ca3af', border: `1px solid ${CONFIDENCE_COLOR[rule.confidence] || '#9ca3af'}40` }}>
                ◎ {rule.confidence} confidence
              </span>
            )}
            {rule.version && <span className="vrd-badge vrd-badge-neutral">v{rule.version}</span>}
          </div>
        </div>
        {rule.description && <p className="vrd-description">{rule.description}</p>}
        {!rule.description && rule.message && <p className="vrd-description">{rule.message}</p>}
      </div>

      <div className="vrd-body">
        {/* Left column */}
        <div className="vrd-main">
          {/* Vulnerability description */}
          <Section title="Vulnerability" icon="⚠️">
            <p className="vrd-text">{rule.message || 'No description available.'}</p>
            {rule.fix && (
              <div className="vrd-fix-box">
                <span className="vrd-fix-label">✅ Remediation</span>
                <p className="vrd-text">{rule.fix}</p>
              </div>
            )}
          </Section>

          {/* Detection query */}
          {rule.query && (
            <Section title="Detection Pattern" icon="🔍">
              <p className="vrd-text vrd-text-sm">
                This rule uses Vigil DSL to detect the pattern. The query runs against your codebase using the opengrep engine.
              </p>
              <CodeBlock code={rule.query.trim()} language="vigil" />
            </Section>
          )}

          {/* Full rule source */}
          {rule.rawContent && (
            <Section title="Rule Source" icon="📄">
              <details className="vrd-details">
                <summary className="vrd-summary">View full <code>{rule.fileName}</code></summary>
                <CodeBlock code={rule.rawContent.trim()} language="vigil" />
              </details>
            </Section>
          )}
        </div>

        {/* Right sidebar */}
        <aside className="vrd-sidebar">
          {/* Metadata card */}
          <div className="vrd-card">
            <h3 className="vrd-card-title">Rule Details</h3>
            <dl className="vrd-dl">
              <dt>Rule ID</dt>
              <dd><code className="vrd-code-inline">{rule.id}</code></dd>
              {rule.packId && <><dt>Pack</dt><dd><Link to={`/vigil/rules?pack=${rule.packId}`} className="vrd-link">{rule.packId}</Link></dd></>}
              <dt>Severity</dt>
              <dd><SeverityBadge severity={rule.severity} /></dd>
              <dt>Confidence</dt>
              <dd>{rule.confidence || 'medium'}</dd>
              {rule.version && <><dt>Version</dt><dd>v{rule.version}</dd></>}
              {rule.fileName && <><dt>File</dt><dd><code className="vrd-code-inline">{rule.fileName}</code></dd></>}
            </dl>
          </div>

          {/* Languages */}
          {rule.languages && rule.languages.length > 0 && (
            <div className="vrd-card">
              <h3 className="vrd-card-title">Languages</h3>
              <div className="vrd-tag-list">
                {rule.languages.map(lang => (
                  <TagBadge key={lang} label={LANG_ICONS[lang.toLowerCase()] ? `${LANG_ICONS[lang.toLowerCase()]} ${lang}` : lang} color="#6366f1" />
                ))}
              </div>
            </div>
          )}

          {/* Security references */}
          {((rule.cwe && rule.cwe.length > 0) || (rule.owasp && rule.owasp.length > 0)) && (
            <div className="vrd-card">
              <h3 className="vrd-card-title">Security References</h3>
              {rule.cwe && rule.cwe.length > 0 && (
                <div className="vrd-ref-group">
                  <span className="vrd-ref-label">CWE</span>
                  <div className="vrd-tag-list">
                    {rule.cwe.map(cwe => (
                      <a key={cwe} href={`https://cwe.mitre.org/data/definitions/${cwe.replace(/\D/g,'')}.html`} target="_blank" rel="noopener noreferrer">
                        <TagBadge label={cwe} color="#7c3aed" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {rule.owasp && rule.owasp.length > 0 && (
                <div className="vrd-ref-group">
                  <span className="vrd-ref-label">OWASP</span>
                  <div className="vrd-tag-list">
                    {rule.owasp.map(ow => (
                      <TagBadge key={ow} label={ow} color="#ea580c" />
                    ))}
                  </div>
                </div>
              )}
              {rule.frameworks && rule.frameworks.length > 0 && (
                <div className="vrd-ref-group">
                  <span className="vrd-ref-label">Frameworks</span>
                  <div className="vrd-tag-list">
                    {rule.frameworks.map(fw => (
                      <TagBadge key={fw} label={fw} color="#059669" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quick actions */}
          <div className="vrd-card">
            <h3 className="vrd-card-title">Actions</h3>
            <div className="vrd-actions">
              <button className="vrd-action-btn" onClick={() => navigate(`/vigil/scan?pack=${rule.packId || ''}`)}>
                🔍 Scan with this pack
              </button>
              <button className="vrd-action-btn vrd-action-secondary" onClick={() => navigate(`/vigil/rulesmith?ruleId=${rule.id}`)}>
                ✏️ Edit in RuleSmith
              </button>
              <button className="vrd-action-btn vrd-action-secondary" onClick={() => navigate('/vigil/rules')}>
                ← Back to Rules
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
